import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Drawer, Tabs, Spin, Layout, notification } from 'antd';
import { selectAllocationHistory, getAllocationHistory } from "../../store/slices/timeseriesModels.slice";
import { resolveSeries, maybeFilterChartData } from "./utils";
import AllocationContainer from "./AllocationContainer";
import {sampleColors} from "../../utils/colors";
import AllocationSider from "./components/AllocationSider";
import {createSequentialBlues} from "../../utils/colors";


const { TabPane } = Tabs;

const TabConfigs = [
    { key: 'style', label: 'Style', dataKey: 'style', innerKey: 'w' },
    { key: 'size', label: 'Size', dataKey: 'size', innerKey: 'w' },
    { key: 'portfolio', label: 'Portfolio', dataKey: 'portfolio', innerKey: 'wgt' }
]


const PortfolioAllocations = ({ pid, allowScreenshot, chartRef, showOrient=false }) => {
    const dispatch = useDispatch();
    const allocationSeries = useSelector(state => selectAllocationHistory(state, pid));
    const [showSider, setShowSider] = useState(false);
    const [activeTab, setActiveTab] = useState('style');
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [seriesValues, setSeriesValues] = useState([]);
    const [filteredChartData, setFilteredChartData] = useState([]);
    const [fieldNames, setFieldNames] = useState([]);
    const [chartType, setChartType] = useState('indices'); // ['bar', 'pie']
    const [validRange, setValidRange] = useState({startDate: undefined, endDate: undefined});
    const [dateRange, setDateRange] = useState({ startDate: undefined, endDate: undefined });
    const [skipInterval, setSkipInterval] = useState(1);
    const [uniqueColors, setUniqueColors] = useState({});
    const [colorConfig, setColorConfig] = useState({
        hue: 'random',
        luminosity: null
    });
    const [barPadding, setBarPadding] = useState(0.05);
    const [barOpacity, setBarOpacity] = useState(0.8);
    const syncRef = useRef(chartRef || null);

        // Trigger notification on first visit
    useEffect(() => {
        const hasSeenNotification = localStorage.getItem('allocation_notification_seen');
        if (!hasSeenNotification) {
            notification.info({
                message: 'Welcome to Portfolio Allocations',
                description: `The allocations over time show a historical representation of the changes within the portfolio by size, style, and holding.`,
                placement: 'topRight',
                duration: 5,
            });
            localStorage.setItem('allocation_notification_seen', 'true');
        }
    }, []);


    useEffect(() => {
        setLoading(true);
        dispatch(getAllocationHistory({ pid }))
            .then(() => {})
            .finally(() => setLoading(false));
    }, [pid, dispatch]);

    useEffect(() => {
        if (allocationSeries) {
            setValidRange({
                startDate: allocationSeries.chart[0].timestamp,
                endDate: allocationSeries.chart[allocationSeries.chart.length - 1].timestamp
            });
            updateChartData();

        }
    }, [allocationSeries, activeTab, dateRange, skipInterval]);

    const generateColors = (n)=>{
        return sampleColors(n, colorConfig.hue, colorConfig.luminosity);
    }

    useEffect(() => {
        const newFields = fieldNames.filter(fn => !uniqueColors[fn]);
        if (newFields.length > 0) {
            const colors = generateColors(newFields.length);
            const newColors = Object.fromEntries(newFields.map((fn, idx) => [fn, colors[idx]]));
            setUniqueColors(prevColors => ({...prevColors, ...newColors}));
        }
    }, [fieldNames]);

    const rollColors = () => {
        const colors = generateColors(fieldNames.length)
        const newColors = Object.fromEntries(fieldNames.map((fn, idx) => [fn, colors[idx]]));
        setUniqueColors(newColors);
    };

    const updateChartData = () => {
        const currentTabConfig = TabConfigs.find(tab => tab.key === activeTab);
        if (currentTabConfig && allocationSeries[currentTabConfig.dataKey]) {
            const [tableData, fieldnames, newChartData] = resolveSeries(allocationSeries[currentTabConfig.dataKey], {
                key: currentTabConfig.innerKey,
            });
            setTableData(tableData);
            setFieldNames(fieldnames);
            let safeSkipInterval = skipInterval;
            if (activeTab === 'portfolio' && skipInterval < 10) {
                safeSkipInterval = 10;
            }
            const filterKws = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                skipInterval: safeSkipInterval
            }
            const newFilteredData = maybeFilterChartData({data: newChartData, ...filterKws});
            const filteredSeriesData = maybeFilterChartData({
                data: allocationSeries.chart.map(x=>{return{id:x.timestamp, ...x}}), ...filterKws});
            setFilteredChartData(newFilteredData);
            setSeriesValues(filteredSeriesData);
        }
    };
    const handleDateRangeChange = (startDate, endDate) => {
        setDateRange({
            startDate: startDate ? startDate : validRange.startDate,
            endDate: endDate ? endDate:validRange.endDate
        });
    };
    const handleSkipIntervalChange = (value) => {
        setSkipInterval(value);
    }


    const callbackOnRangeChange = (start, end)=>{
        syncRef?.current?.selectRange(start, end);
    }


    const renderTabContent = () => {
        return (
            loading ? <Spin size="large" /> : tableData.length > 0 ? (
                <AllocationContainer
                    chartData={filteredChartData}
                    fieldNames={fieldNames}
                    tableData={tableData}
                    colorMapping={uniqueColors}
                    barPadding={barPadding}
                    barOpacity={barOpacity}
                    chartType={chartType}
                    setChartType={setChartType}
                    showSider={showSider}
                    toggleShowSider={()=>setShowSider(!showSider)}
                    seriesValues={seriesValues.map(x=>{return[x.timestamp, x?.close]})}
                    allowScreenshot={allowScreenshot}
                    portfolioSeries={allocationSeries.portfolio}
                    syncRef={syncRef}
                    callbackOnRangeChange={callbackOnRangeChange}
                />
            ) : <p>No data available</p>
        );
    }

    return (
        <Layout style={{background: "#FFF"}}>
            <Layout.Content style={{background: "#FFF"}}>
                <Tabs
                    tabPosition={'left'}
                    bordered={false}
                    defaultActiveKey="style" onChange={(key)=>setActiveTab(key)} centered={true}>
                    {TabConfigs.map(tabConfig => (
                        <TabPane tab={tabConfig.label} key={tabConfig.key}>
                            {renderTabContent()}
                        </TabPane>
                    ))}
                </Tabs>
            </Layout.Content>
            <Drawer
                open={showSider}
                width={400}
                placement="left"
                onClose={()=>setShowSider(false)}
            >
                {!loading && allocationSeries && (
                        <AllocationSider {...{
                            validRange, dateRange, handleDateRangeChange, handleSkipIntervalChange, colorConfig, setColorConfig,
                            barOpacity, setBarOpacity, barPadding, setBarPadding, rollColors
                        }} />
                )}
            </Drawer>
        </Layout>
    );
};

export default PortfolioAllocations;